import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Save, Info, DollarSign } from "lucide-react";

const FIRMS = [
  { id: "ftmo",         label: "FTMO",         hint: "https://ftmo.com/partners" },
  { id: "fundednext",   label: "FundedNext",   hint: "https://fundednext.com/affiliate" },
  { id: "the5ers",      label: "The 5%ers",    hint: "https://the5ers.com/affiliate" },
  { id: "myforexfunds", label: "MyForexFunds", hint: "https://myforexfunds.com/affiliates" },
  { id: "topstep",      label: "TopStep",      hint: "https://topstep.com/affiliates" },
  { id: "e8",           label: "E8 Funding",   hint: "https://e8markets.com/affiliates" },
  { id: "ftuk",         label: "FTUK",         hint: "https://ftuk.com/affiliate" },
  { id: "fundingpips",  label: "FundingPips",  hint: "https://fundingpips.com/affiliate" },
];

export default function AdminAffiliates() {
  const [ids, setIds] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem("auth_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/affiliates`, { headers: authHeader() });
        setIds(r.data.ids || {});
      } catch (e) {
        toast.error("Accès refusé (fondateur requis)");
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/affiliates`, { ids }, { headers: authHeader() });
      toast.success("Codes d'affiliation enregistrés");
    } catch (e) {
      toast.error("Erreur : " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const count = Object.values(ids).filter(Boolean).length;

  return (
    <div className="p-6 max-w-3xl" data-testid="admin-affiliates-page">
      <div className="flex items-center gap-3 mb-2">
        <DollarSign size={22} className="text-accent-green" />
        <h1 className="font-heading font-bold text-2xl">Gestion des affiliations</h1>
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-accent-blue/15 text-accent-blue border border-accent-blue/30">
          {count}/{FIRMS.length} configurés
        </span>
      </div>
      <p className="text-white/60 text-sm mb-6 max-w-2xl">
        Collez ici vos codes affiliés pour chaque prop firm. Les liens sur <code className="font-mono text-xs bg-white/5 px-1 rounded">/prop-firms</code>
        se mettront à jour instantanément — vous commencerez à toucher vos commissions dès la prochaine inscription.
      </p>

      <div className="panel p-5 mb-4">
        <div className="text-xs text-white/50 bg-white/5 border border-white/10 rounded-md p-3 mb-5 flex gap-2">
          <Info size={14} className="text-accent-blue shrink-0 mt-0.5" />
          <div>
            Le code affilié est généralement le <strong className="text-white">segment après <code className="font-mono">?ref=</code></strong> ou{" "}
            <code className="font-mono">?affid=</code> dans le lien que la prop firm vous fournit après inscription à leur programme.
            <br />Exemple pour FTMO : <code className="font-mono">FLTRAD2026</code>.
          </div>
        </div>

        {loading ? (
          <div className="text-white/50 text-sm">Chargement…</div>
        ) : (
          <div className="space-y-4">
            {FIRMS.map((f) => (
              <div key={f.id} className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-2">
                <label className="text-sm font-medium text-white/70">{f.label}</label>
                <div>
                  <input
                    type="text"
                    className="input w-full font-mono text-sm"
                    placeholder="Vide = lien direct (sans commission)"
                    value={ids[f.id] || ""}
                    onChange={(e) => setIds({ ...ids, [f.id]: e.target.value })}
                    data-testid={`affiliate-input-${f.id}`}
                  />
                  <a href={f.hint} target="_blank" rel="noopener noreferrer" className="text-[10px] text-white/40 hover:text-accent-blue mt-1 inline-block">
                    Programme affilié {f.label} ↗
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button className="btn btn-primary" onClick={save} disabled={saving}
                data-testid="admin-affiliates-save">
          <Save size={14} /> {saving ? "Enregistrement..." : "Enregistrer les codes"}
        </button>
      </div>
    </div>
  );
}
